import React, { useState, useContext, useEffect } from 'react';
import Panel from '../../../../components/Panel';
import AuthContext from '../../../../contexts/AuthContext';
import PlaidButton from '../../../../components/PlaidButton';
import PlaidReconnectButton from '../../../../components/PlaidReconnectButton';
import useAPI from '../../../../hooks/useAPI';
import useErrors from '../../../../hooks/useErrors';
import PanelTable from '../../../../components/Panel/PanelTable';
import moment from 'moment';
import Button from '../../../../components/Button';
import Badge from '../../../../components/Badge';
import Select from '../../../../components/Select';
import ComponentBusy from '../../../../components/ComponentBusy';
import RoundedButton from '../../../../components/RoundedButton';

export default function PaymentSettings ({ onLoad, onLoadEnd }) {
    const { system } = useContext(AuthContext);

    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [paymentSettings, setPaymentSettings] = useState({});
    const [savingPaymentSettings, setSavingPaymentSettings] = useState(false);
    const [removingPaymentMethod, setRemovingPaymentMethod] = useState(false);
    const [changingPrimaryPaymentMethod, setChangingPrimaryPaymentMethod] = useState(false);

    const api = useAPI();
    const _errors = useErrors();

    const load = ()=>{
        onLoad();
        setLoading(true);
        
        api.json('GET', '/payment-methods/summary')
           .then(r=>r.json())
           .then((r)=>{
              if(r.errors){
                _errors(r.errors);
                return;
              }
              setAccounts(r.data.payment_methods || []);
              setPaymentSettings(r.data.payment_settings)
           })
           .catch(e=>alert(e))
           .then(()=>{
                onLoadEnd();
                setLoading(false);
            })
    }

    useEffect(()=>{
        load();
        // eslint-disable-next-line
    }, []);

    const removeAccount = (theAccount) => {
        setRemovingPaymentMethod(true);

        api.delete('/payment-methods/' + theAccount.id)
            .then(r => r.json())
            .then((r) => {
                if (r.data) {
                    setAccounts(r.data || accounts);
                }
            })
            .catch(e => alert(e))
            .finally(() => setRemovingPaymentMethod(false));
    };

    const changePrimaryAccount = (theAccount) => {
        setChangingPrimaryPaymentMethod(true);

        api.post('/payment-methods/' + theAccount.id + '/primary')
            .then(r => r.json())
            .then((r) => {
                if (r.data) {
                    setAccounts(r.data || accounts);
                }
            })
            .catch(e => alert(e))
            .finally(() => setChangingPrimaryPaymentMethod(false));
    };

    const savePaymentSettings = () => {
        setSavingPaymentSettings(true);

        api.put('/payment-methods/settings', {
            ...paymentSettings
        }).then(r => {
            console.log(r);
            if (r.status !== 200) {
                throw new Error('An unknown error has occurred.');
            }
        })
            .catch(e => alert(e))
            .finally(() => setSavingPaymentSettings(false));
    };

    if (loading) {
        return <ComponentBusy />;
    }

    if (!system.is_owner) {
        return null;
    }

    return (
        <div className='account-settings'>
            <Panel
                title="Payment Distribution"
                headerStyle={{
                    borderBottomWidth: 1,
                    borderStyle: 'solid',
                    borderColor: '#E2E3E8',
                    padding: 20,
                    marginBottom: 20
                }}
            >
                <Select
                    label="Default Payment Method"
                    options={[
                        { label: 'Check', value: 'CHECK' },
                        { label: 'ACH/Wire (Requires Linked Bank Account)', value: 'ACH' }
                    ]}
                    value={paymentSettings.method}
                    onChange={(e) => {
                        setPaymentSettings({
                            ...paymentSettings,
                            method: e.target.value
                        });
                    }}
                />
                <div className='row'>
                    <div className='col-12'>
                        <RoundedButton
                            solid
                            onClick={savePaymentSettings}
                            busy={savingPaymentSettings}
                        >
                            Save Changes
                        </RoundedButton>
                    </div>
                </div>
            </Panel>
            <Panel
                title="Linked Bank Accounts"
                contentStyle={{
                    padding: accounts.length ? 0 : 20,
                }}
                headerStyle={
                    accounts.length ? {
                        padding: '20px 20px 0 20px'
                    } : {
                        borderBottomWidth: 1,
                        borderStyle: 'solid',
                        borderColor: '#E2E3E8',
                        padding: 20
                    }
                }
            >
                {
                    accounts.length ?
                        <PanelTable
                            headers={['Account', 'Last 4', 'Created', '']}
                            items={accounts}
                            render={(a) => {
                                const addedAt = moment(a.created_at);
                                const remove = (
                                    <Button className='button-danger' onClick={() => {
                                        if (window.confirm('Are you sure you\'d like to remove this account?')) {
                                            removeAccount(a);
                                        }
                                    }} disabled={removingPaymentMethod || changingPrimaryPaymentMethod}>
                                        Remove
                                    </Button>
                                );

                                return (
                                    <>
                                        <td>
                                            <span>{a.account_name} {a.status === 'UPDATE_REQUIRED' ? <Badge type="danger">Re-Connection Required</Badge> : null} {a.is_primary ? <Badge type="success">Primary</Badge> : null} </span>
                                        </td>
                                        <td><span>{a.account_ends_in}</span></td>
                                        <td><span>{addedAt.format('MMM DD, YYYY')}</span></td>
                                        <td width="250">
                                            <span style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', textAlign: 'center', gap: 10 }}>
                                                {
                                                    a.status === 'ACTIVE' && !a.is_primary ?
                                                        <span>
                                                            <Button
                                                                disabled={removingPaymentMethod || changingPrimaryPaymentMethod}
                                                                onClick={() => {
                                                                    if (window.confirm('Are you sure? The primary account will be used for all future deposits.')) {
                                                                        changePrimaryAccount(a);
                                                                    }
                                                                }}
                                                            >
                                                                Make Primary
                                                            </Button>
                                                            {remove}
                                                        </span>
                                                        : null
                                                }

                                                {
                                                    a.status === 'UPDATE_REQUIRED' ?
                                                        <span style={{ textAlign: 'center' }}>
                                                            <PlaidReconnectButton
                                                                accountId={a.id}
                                                            />
                                                            {!a.is_primary && remove}
                                                        </span>
                                                        : null
                                                }
                                            </span>
                                        </td>
                                    </>
                                );
                            }}
                        />
                        : (
                            <div style={{ display: 'flex', padding: '20px', flex: 1, justifyContent: 'center', alignItems: 'center', flexDirection: 'column', textAlign: 'center', gap: 20 }}>
                                <span>
                                    Streamline your payment experience with CSG Direct Pay. Link your bank account effortlessly using the power of Plaid to receive ACH payments directly, ensuring faster transactions and top-notch security. <a href="https://srechelp.carbonsolutionsgroup.com/hc/en-us/articles/17942957381908-Receiving-SREC-Payments-through-ACH" target="_blank" rel="noreferrer">Click here to read more.</a>
                                </span>
                                <PlaidButton />
                            </div>
                        )
                }

            </Panel>
            {
                accounts.length ?
                    <div style={{ display: 'flex', paddingBottom: 20 }}>
                        <PlaidButton />
                    </div>
                    : null
            }
        </div>
    );
};